import { useState } from 'react'
import type { Runtime, RuntimeCreated, RuntimeTaskKind } from '@workflow/contracts'
import { RUNTIME_TASK_KINDS } from '@workflow/contracts'
import { Button, Checkbox, ConfirmButton, EmptyNote, Field, Icons, Loading, StateNote, TextField } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useCreateRuntime, useRetireRuntime, useRuntimes, useUpdateRuntime, useSession } from '@/lib/api/hooks'
import { formatRelative, formatTimestamp } from '@/lib/format'

/**
 * The machines that execute the studio's work, administered. Enrolling cuts
 * a runtime its own passport — `rt_…`, shown exactly once — that walks one
 * surface only: heartbeat, claim, report. Retirement is one-way, like a
 * token's revocation: the key dies, the record stands.
 */

const KIND_NAMES: Record<RuntimeTaskKind, string> = {
  delivery: 'Delivery',
}

export function RuntimesPane() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const runtimes = useRuntimes()
  const [enrolling, setEnrolling] = useState(false)
  const [created, setCreated] = useState<RuntimeCreated | null>(null)

  if (!isAdmin) {
    return (
      <StateNote
        title="This part of settings is for administrators."
        detail="Runtimes are enrolled by administrators. A runtime executes the work the console dispatches to it."
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {created ? (
        <ShowOnce runtime={created} onDone={() => setCreated(null)} />
      ) : enrolling ? (
        <EnrollForm
          onDone={(runtime) => {
            setEnrolling(false)
            setCreated(runtime)
          }}
          onCancel={() => setEnrolling(false)}
        />
      ) : (
        <div>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setEnrolling(true)}>
            Enroll runtime
          </Button>
          <p className="mt-1 max-w-[60ch] text-caption text-muted-foreground">
            A runtime runs on its own machine and carries its own key. The secret is shown once, right after enrollment.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">The machines</h3>
        <div className="mt-1.5">
          {runtimes.isLoading ? (
            <Loading label="Loading runtimes" />
          ) : runtimes.error ? (
            <StateNote title="The runtimes could not be loaded." detail={describeError(runtimes.error)} />
          ) : (runtimes.data?.length ?? 0) === 0 ? (
            <EmptyNote
              title="No runtimes enrolled yet."
              detail="Enroll one when the studio's work needs a machine to run on."
            />
          ) : (
            <ul className="flex flex-col">
              {runtimes.data!.map((runtime) => (
                <RuntimeRow key={runtime.id} runtime={runtime} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** The enrollment moment: the secret, in full, for the last time. */
function ShowOnce({ runtime, onDone }: { runtime: RuntimeCreated; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <p className="text-ui font-medium text-foreground">Runtime enrolled: {runtime.name}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border bg-surface-sunken px-2 py-1.5 font-mono text-[13px] text-foreground">
          {runtime.token}
        </code>
        <Button
          size="xs"
          variant="ghost"
          icon={<Icons.copy />}
          onClick={() => {
            void navigator.clipboard.writeText(runtime.token)
            setCopied(true)
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-2 text-caption text-danger">
        Copy it now. This is the only time the secret is shown — the record keeps only its prefix.
      </p>
      <p className="mt-1 max-w-[60ch] text-caption text-muted-foreground">
        The machine presents it as <code className="font-mono">Authorization: Bearer rt_…</code> on the runtime surface:
        heartbeat, claim, report.
      </p>
      <div className="mt-3">
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}

function EnrollForm({ onDone, onCancel }: { onDone: (runtime: RuntimeCreated) => void; onCancel: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [capabilities, setCapabilities] = useState<RuntimeTaskKind[]>(['delivery'])
  const create = useCreateRuntime()

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    create.mutate(
      { name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), capabilities },
      { onSuccess: (data) => onDone(data as RuntimeCreated) },
    )
  }

  const toggle = (kind: RuntimeTaskKind) =>
    setCapabilities((current) => (current.includes(kind) ? current.filter((entry) => entry !== kind) : [...current, kind]))

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <Field label="Name" htmlFor="runtime-name" hint="The machine's name on the record and in the audit trail.">
        <TextField
          id="runtime-name"
          value={name}
          autoFocus
          placeholder="e.g. The VPS in Frankfurt"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Description" htmlFor="runtime-description" hint="Where it lives, what it is for — optional.">
        <TextField
          id="runtime-description"
          value={description}
          placeholder="Optional"
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <fieldset className="min-w-0">
        <legend className="text-micro text-muted-foreground uppercase">Kinds it can run</legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {RUNTIME_TASK_KINDS.map((kind) => (
            <Checkbox key={kind} className="rounded-lg px-2 py-2 hover:bg-tint"
              checked={capabilities.includes(kind)} onChange={() => toggle(kind)}>
              <span className="font-medium">{KIND_NAMES[kind]}</span>
            </Checkbox>
          ))}
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={create.isPending} disabled={!name.trim() || capabilities.length === 0}>
          Enroll
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {create.error ? <p className="text-caption text-danger">{describeError(create.error)}</p> : null}
      </div>
    </form>
  )
}

function RuntimeRow({ runtime }: { runtime: Runtime }) {
  const retire = useRetireRuntime()
  const [editing, setEditing] = useState(false)
  const retired = runtime.status === 'retired'

  // The machine's presence, read the way presence is read everywhere here:
  // how long ago it last said it was alive.
  const heartbeat =
    runtime.lastHeartbeatAt === null
      ? retired
        ? 'no heartbeat on record'
        : 'has not called in yet'
      : `heartbeat ${formatRelative(runtime.lastHeartbeatAt)}`

  if (editing) {
    return (
      <li className="border-b border-hairline py-3 last:border-0">
        <EditRuntime runtime={runtime} onDone={() => setEditing(false)} />
      </li>
    )
  }

  return (
    <li className="border-b border-hairline last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">
            {runtime.name}
            {retired ? <span className="ml-2 text-caption text-muted-foreground">retired</span> : null}
          </span>
          <span className="block truncate font-mono text-caption text-muted-foreground">
            {runtime.tokenPrefix}… · {runtime.capabilities.map((kind) => KIND_NAMES[kind] ?? kind).join(', ')}
          </span>
          {runtime.description ? (
            <span className="block truncate text-caption text-muted-foreground">{runtime.description}</span>
          ) : null}
        </span>
        <span className="text-caption text-muted-foreground" title="Last heartbeat">
          {heartbeat}
        </span>
        <span className="text-caption text-muted-foreground" title="Enrolled">
          enrolled {formatTimestamp(runtime.enrolledAt)}
        </span>
        {!retired ? (
          <span className="flex items-center gap-1.5">
            <Button size="xs" variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <ConfirmButton
              label="Retire"
              confirmLabel="Really retire?"
              busy={retire.isPending}
              onConfirm={() => retire.mutate({ id: runtime.id })}
            />
          </span>
        ) : null}
      </div>
      {retire.error ? <p className="px-1 pb-2 text-caption text-danger">{describeError(retire.error)}</p> : null}
    </li>
  )
}

function EditRuntime({ runtime, onDone }: { runtime: Runtime; onDone: () => void }) {
  const [name, setName] = useState(runtime.name)
  const [description, setDescription] = useState(runtime.description ?? '')
  const [capabilities, setCapabilities] = useState<RuntimeTaskKind[]>(runtime.capabilities)
  const update = useUpdateRuntime()

  const nameChanged = name.trim() !== runtime.name
  const descriptionChanged = (description.trim() || null) !== runtime.description
  const capabilitiesChanged =
    capabilities.length !== runtime.capabilities.length ||
    RUNTIME_TASK_KINDS.some((kind) => capabilities.includes(kind) !== runtime.capabilities.includes(kind))
  const unchanged = !nameChanged && !descriptionChanged && !capabilitiesChanged

  const toggle = (kind: RuntimeTaskKind) =>
    setCapabilities((current) => (current.includes(kind) ? current.filter((entry) => entry !== kind) : [...current, kind]))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    update.mutate(
      {
        id: runtime.id,
        input: {
          ...(nameChanged ? { name: name.trim() } : {}),
          ...(descriptionChanged ? { description: description.trim() || null } : {}),
          ...(capabilitiesChanged ? { capabilities } : {}),
        },
      },
      { onSuccess: onDone },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <Field label="Name" htmlFor={`runtime-name-${runtime.id}`}>
        <TextField id={`runtime-name-${runtime.id}`} value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Description" htmlFor={`runtime-description-${runtime.id}`}>
        <TextField
          id={`runtime-description-${runtime.id}`}
          value={description}
          placeholder="Optional"
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <fieldset className="min-w-0">
        <legend className="text-micro text-muted-foreground uppercase">Kinds it can run</legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {RUNTIME_TASK_KINDS.map((kind) => (
            <Checkbox key={kind} className="rounded-lg px-2 py-2 hover:bg-tint"
              checked={capabilities.includes(kind)} onChange={() => toggle(kind)}>
              <span className="font-medium">{KIND_NAMES[kind]}</span>
            </Checkbox>
          ))}
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={update.isPending} disabled={!name.trim() || unchanged}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {update.error ? <p className="text-caption text-danger">{describeError(update.error)}</p> : null}
      </div>
    </form>
  )
}
